import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ArrowLeft, FileText, Layers, Upload,
  Plus, Trash2, ChevronDown, AlertCircle, CheckCircle2,
  Calendar, Boxes, Activity, ClipboardList, Truck, Banknote,
  TrendingUp, Gauge,
} from 'lucide-react';
import { ScheduleTab, ResourcePlanTab } from './PECESprintTwo';
import PECEPRSTab from './PECEPRSTab';
import {
  RequestsTab, ExecutionTab, CommercialTab, CostControlTab, PHIDashboardTab,
} from './PECETabs';

// ─── Constants ─────────────────────────────────────────────────────────────
const WORK_TYPES = ['electrical', 'mechanical', 'civil', 'mep', 'finishing', 'infrastructure', 'other'];
const CONTRACT_STATUSES = ['draft', 'active', 'on_hold', 'completed', 'terminated'];
const PROC_STRATS = ['purchase', 'company_stock', 'internal_labour', 'rental', 'subcontract'];
const BOQ_STATUSES = ['planned', 'requested', 'delivered', 'installed', 'inspected', 'certified', 'invoiced', 'closed'];
const MILESTONES = ['advance', 'material_delivery', 'installation', 'testing', 'retention', 'other'];
const TEAM_ROLES = ['project_manager', 'site_engineer', 'site_supervisor', 'qs_engineer', 'safety_officer', 'foreman', 'other'];

const STATUS_COLORS = {
  planned: 'bg-slate-100 text-slate-700',
  requested: 'bg-blue-100 text-blue-700',
  delivered: 'bg-cyan-100 text-cyan-700',
  installed: 'bg-indigo-100 text-indigo-700',
  inspected: 'bg-violet-100 text-violet-700',
  certified: 'bg-emerald-100 text-emerald-700',
  invoiced: 'bg-amber-100 text-amber-700',
  closed: 'bg-gray-200 text-gray-800',
};

const SPRINTS_LATER = []; // kept for backward-compat; legacy "coming soon" rail removed in Phase 1 closure.

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// ─── Main Page ─────────────────────────────────────────────────────────────
const PECEWorkspace = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('data');
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [p, s] = await Promise.all([
        axiosInstance.get(`/projects/${projectId}`),
        axiosInstance.get(`/pece/projects/${projectId}/summary`),
      ]);
      setProject(p.data);
      setSummary(s.data);
    } catch (e) {
      toast.error('فشل تحميل بيانات المشروع');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, [projectId]);

  if (loading) return <div className="p-8 text-center">جاري التحميل...</div>;
  if (!project) return <div className="p-8 text-center text-red-600">المشروع غير موجود</div>;

  return (
    <div data-testid="pece-workspace" className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="pece-back-btn"
              onClick={() => navigate('/projects')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="text-xs text-indigo-600 font-semibold uppercase tracking-wider">
                PECE · Project Execution & Cost Engine
              </div>
              <h1 className="text-xl font-bold text-gray-900" data-testid="pece-project-name">
                {project.name}
              </h1>
              <div className="text-sm text-gray-500">
                {project.client_name} · Contract: {fmt(project.budget)} SAR
              </div>
            </div>
          </div>
          <SummaryStrip s={summary} />
        </div>

        {/* Tabs — grouped in 3 sections (Planning / Execution / Control). */}
        <div className="max-w-7xl mx-auto px-6 flex gap-4 overflow-x-auto items-end pb-1">
          <TabGroup label="Planning">
            <TabBtn id="data" active={activeTab} onClick={setActiveTab} icon={<FileText className="w-4 h-4" />}>Contract & BOQ</TabBtn>
            <TabBtn id="schedule" active={activeTab} onClick={setActiveTab} icon={<Calendar className="w-4 h-4" />}>Schedule</TabBtn>
            <TabBtn id="resources" active={activeTab} onClick={setActiveTab} icon={<Boxes className="w-4 h-4" />}>Resources</TabBtn>
          </TabGroup>
          <TabGroup label="Execution">
            <TabBtn id="prs" active={activeTab} onClick={setActiveTab} icon={<Activity className="w-4 h-4" />}>PRS</TabBtn>
            <TabBtn id="requests" active={activeTab} onClick={setActiveTab} icon={<ClipboardList className="w-4 h-4" />}>Requests</TabBtn>
            <TabBtn id="execution" active={activeTab} onClick={setActiveTab} icon={<Truck className="w-4 h-4" />}>MIR</TabBtn>
            <TabBtn id="commercial" active={activeTab} onClick={setActiveTab} icon={<Banknote className="w-4 h-4" />}>Commercial</TabBtn>
          </TabGroup>
          <TabGroup label="Control">
            <TabBtn id="cost" active={activeTab} onClick={setActiveTab} icon={<TrendingUp className="w-4 h-4" />}>Cost Control</TabBtn>
            <TabBtn id="phi" active={activeTab} onClick={setActiveTab} icon={<Gauge className="w-4 h-4" />}>PHI Dashboard</TabBtn>
          </TabGroup>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto p-6">
        {activeTab === 'data' && (
          <DataTab projectId={projectId} contractValue={project.budget} onChange={loadAll} />
        )}
        {activeTab === 'schedule' && (
          <ScheduleTab projectId={projectId} onChange={loadAll} />
        )}
        {activeTab === 'resources' && (
          <ResourcePlanTab projectId={projectId} onChange={loadAll} />
        )}
        {activeTab === 'prs' && (
          <PECEPRSTab projectId={projectId} />
        )}
        {activeTab === 'requests' && (
          <RequestsTab projectId={projectId} />
        )}
        {activeTab === 'execution' && (
          <ExecutionTab projectId={projectId} />
        )}
        {activeTab === 'commercial' && (
          <CommercialTab projectId={projectId} />
        )}
        {activeTab === 'cost' && (
          <CostControlTab projectId={projectId} />
        )}
        {activeTab === 'phi' && (
          <PHIDashboardTab projectId={projectId} />
        )}
      </div>
    </div>
  );
};

const TabGroup = ({ label, children }) => (
  <div className="flex flex-col items-start" data-testid={`pece-group-${label.toLowerCase()}`}>
    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 px-2 pt-2 pb-0.5">
      {label}
    </div>
    <div className="flex gap-1 border-l border-r border-slate-200 px-1">
      {children}
    </div>
  </div>
);

const TabBtn = ({ id, active, onClick, children, icon, disabled }) => (
  <button
    data-testid={`pece-tab-${id}`}
    onClick={() => !disabled && onClick(id)}
    disabled={disabled}
    className={`px-4 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 whitespace-nowrap ${
      active === id
        ? 'border-indigo-600 text-indigo-700'
        : disabled
        ? 'border-transparent text-gray-400 cursor-not-allowed'
        : 'border-transparent text-gray-600 hover:text-gray-900'
    }`}
  >
    {icon}{children}
  </button>
);

const SummaryStrip = ({ s }) => {
  if (!s) return null;
  return (
    <div className="hidden md:flex items-center gap-6 text-xs">
      <Stat label="BOQ Total" value={`${fmt(s.boq_total_value)} SAR`} />
      <Stat label="Variance" value={`${fmt(s.boq_variance)} SAR`}
            tone={Math.abs(s.boq_variance) < 1 ? 'good' : 'warn'} />
      <Stat label="BOQ Items" value={s.boq_items_count} />
      <Stat label="Team" value={s.team_count} />
      <Stat label="Pay Terms" value={`${s.payment_terms_total_pct}%`}
            tone={s.payment_terms_valid ? 'good' : 'warn'} />
    </div>
  );
};

const Stat = ({ label, value, tone }) => (
  <div className="text-right">
    <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className={`text-sm font-bold ${tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-gray-900'}`}>
      {value}
    </div>
  </div>
);

// ─── Tab 1 — Data ──────────────────────────────────────────────────────────
const DataTab = ({ projectId, contractValue, onChange }) => {
  const [section, setSection] = useState('contract');
  return (
    <div className="space-y-6">
      <div className="flex gap-2 bg-white rounded-lg p-1 border w-fit">
        {[
          { k: 'contract', label: 'Contract Data' },
          { k: 'boq', label: 'BOQ' },
          { k: 'payment', label: 'Payment Terms' },
          { k: 'team', label: 'Team' },
        ].map((s) => (
          <button
            key={s.k}
            data-testid={`pece-section-${s.k}`}
            onClick={() => setSection(s.k)}
            className={`px-4 py-2 text-sm rounded-md transition ${
              section === s.k ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === 'contract' && <ContractSection projectId={projectId} onChange={onChange} />}
      {section === 'boq' && <BOQSection projectId={projectId} onChange={onChange} />}
      {section === 'payment' && <PaymentTermsSection projectId={projectId} contractValue={contractValue} onChange={onChange} />}
      {section === 'team' && <TeamSection projectId={projectId} onChange={onChange} />}
    </div>
  );
};

// ─── Contract Section ──────────────────────────────────────────────────────
const ContractSection = ({ projectId, onChange }) => {
  const [data, setData] = useState({
    work_type: 'other', contract_status: 'draft',
    consultant_name: '', consultant_contact: '', notes: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance.get(`/pece/projects/${projectId}/contract`).then((r) => {
      setData({
        work_type: r.data.work_type || 'other',
        contract_status: r.data.contract_status || 'draft',
        consultant_name: r.data.consultant_name || '',
        consultant_contact: r.data.consultant_contact || '',
        notes: r.data.notes || '',
      });
      setLoading(false);
    });
  }, [projectId]);

  const save = async () => {
    try {
      await axiosInstance.put(`/pece/projects/${projectId}/contract`, data);
      toast.success('تم حفظ بيانات العقد');
      onChange();
    } catch (e) { toast.error('فشل الحفظ'); }
  };

  if (loading) return <div>...</div>;

  return (
    <div className="bg-white rounded-xl p-6 border space-y-4">
      <h3 className="text-lg font-semibold mb-2">بيانات العقد</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="نوع العمل">
          <select data-testid="contract-work-type" value={data.work_type}
                  onChange={(e) => setData({ ...data, work_type: e.target.value })}
                  className="form-input w-full">
            {WORK_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </Field>
        <Field label="حالة العقد">
          <select data-testid="contract-status" value={data.contract_status}
                  onChange={(e) => setData({ ...data, contract_status: e.target.value })}
                  className="form-input w-full">
            {CONTRACT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="الاستشاري">
          <input data-testid="contract-consultant-name" type="text" value={data.consultant_name}
                 onChange={(e) => setData({ ...data, consultant_name: e.target.value })}
                 className="form-input w-full" />
        </Field>
        <Field label="بيانات التواصل">
          <input data-testid="contract-consultant-contact" type="text" value={data.consultant_contact}
                 onChange={(e) => setData({ ...data, consultant_contact: e.target.value })}
                 className="form-input w-full" />
        </Field>
      </div>
      <Field label="ملاحظات">
        <textarea data-testid="contract-notes" rows={3} value={data.notes}
                  onChange={(e) => setData({ ...data, notes: e.target.value })}
                  className="form-input w-full" />
      </Field>
      <button data-testid="contract-save-btn" onClick={save}
              className="bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700">
        حفظ
      </button>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

// ─── BOQ Section ───────────────────────────────────────────────────────────
const BOQSection = ({ projectId, onChange }) => {
  const [sets, setSets] = useState([]);
  const [activeSetId, setActiveSetId] = useState(null);
  const [items, setItems] = useState([]);
  const [showNewSet, setShowNewSet] = useState(false);
  const [showNewItem, setShowNewItem] = useState(false);

  const loadSets = async () => {
    const r = await axiosInstance.get(`/pece/projects/${projectId}/boq-sets`);
    setSets(r.data);
    if (!activeSetId && r.data.length > 0) setActiveSetId(r.data[0].id);
  };

  const loadItems = async () => {
    if (!activeSetId) { setItems([]); return; }
    const r = await axiosInstance.get(`/pece/projects/${projectId}/boq-sets/${activeSetId}/items`);
    setItems(r.data);
  };

  useEffect(() => { loadSets(); }, [projectId]);
  useEffect(() => { loadItems(); }, [activeSetId]);

  const activeSet = sets.find((s) => s.id === activeSetId);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeSetId) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axiosInstance.post(
        `/pece/projects/${projectId}/boq-sets/${activeSetId}/upload`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`تم رفع ${r.data.inserted} بند`);
      await loadSets(); await loadItems(); onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الرفع');
    }
    e.target.value = '';
  };

  const deleteItem = async (id) => {
    if (!window.confirm('حذف هذا البند؟')) return;
    try {
      await axiosInstance.delete(`/pece/projects/${projectId}/boq-items/${id}`);
      await loadSets(); await loadItems(); onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحذف');
    }
  };

  return (
    <div className="space-y-4">
      {/* BOQ Sets Selector */}
      <div className="bg-white rounded-xl p-4 border flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-700 mr-2">BOQ:</span>
          {sets.map((s) => (
            <button
              key={s.id}
              data-testid={`boq-set-${s.code}`}
              onClick={() => setActiveSetId(s.id)}
              className={`px-3 py-1.5 text-sm rounded-md transition flex items-center gap-2 ${
                activeSetId === s.id ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="font-semibold">{s.code}</span>
              <span className="text-xs opacity-80">{fmt(s.total_value)}</span>
            </button>
          ))}
          <button
            data-testid="boq-new-set-btn"
            onClick={() => setShowNewSet(true)}
            className="px-3 py-1.5 text-sm rounded-md border border-dashed border-gray-300 text-gray-600 hover:border-indigo-500 hover:text-indigo-600"
          >
            <Plus className="w-4 h-4 inline" /> BOQ جديد
          </button>
        </div>
        {activeSet && (
          <div className="flex items-center gap-2">
            <label className="cursor-pointer bg-emerald-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-emerald-700 flex items-center gap-1">
              <Upload className="w-4 h-4" /> رفع Excel
              <input data-testid="boq-upload-input" type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} className="hidden" />
            </label>
            <button
              data-testid="boq-new-item-btn"
              onClick={() => setShowNewItem(true)}
              className="bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-indigo-700 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" /> بند جديد
            </button>
          </div>
        )}
      </div>

      {/* BOQ Items Tree */}
      {activeSet ? (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-3 font-medium text-gray-700">Code</th>
                <th className="text-left p-3 font-medium text-gray-700">Description</th>
                <th className="text-left p-3 font-medium text-gray-700">Unit</th>
                <th className="text-right p-3 font-medium text-gray-700">Qty</th>
                <th className="text-right p-3 font-medium text-gray-700">Rate</th>
                <th className="text-right p-3 font-medium text-gray-700">Value</th>
                <th className="text-center p-3 font-medium text-gray-700">Status</th>
                <th className="text-center p-3 font-medium text-gray-700">Strategy</th>
                <th className="text-right p-3 font-medium text-gray-700"></th>
              </tr>
            </thead>
            <tbody>
              <BOQTree items={items} onDelete={deleteItem} />
              {items.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-gray-500">
                  لا توجد بنود. ابدأ برفع Excel أو إضافة بند يدوياً.
                </td></tr>
              )}
            </tbody>
            {items.length > 0 && (
              <tfoot className="bg-gray-50 border-t">
                <tr>
                  <td colSpan={5} className="p-3 text-right font-semibold">إجمالي BOQ</td>
                  <td className="p-3 text-right font-bold text-indigo-700">{fmt(activeSet.total_value)}</td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-12 text-center border-2 border-dashed">
          <Layers className="w-10 h-10 mx-auto text-gray-400 mb-2" />
          <p className="text-gray-600">ابدأ بإنشاء BOQ رئيسي</p>
        </div>
      )}

      {showNewSet && (
        <NewSetModal projectId={projectId} hasMain={sets.some((s) => s.set_type === 'main')}
                     onClose={() => setShowNewSet(false)}
                     onCreated={async () => { setShowNewSet(false); await loadSets(); onChange(); }} />
      )}
      {showNewItem && activeSetId && (
        <NewItemModal projectId={projectId} setId={activeSetId} items={items}
                      onClose={() => setShowNewItem(false)}
                      onCreated={async () => { setShowNewItem(false); await loadSets(); await loadItems(); onChange(); }} />
      )}
    </div>
  );
};

// Flatten parent-child tree into ordered rows (DFS)
const flattenBOQ = (items) => {
  const byParent = items.reduce((acc, it) => {
    const k = it.parent_id || 'root';
    if (!acc[k]) acc[k] = [];
    acc[k].push(it);
    return acc;
  }, {});
  const out = [];
  const walk = (parentId, depth) => {
    (byParent[parentId] || []).forEach((it) => {
      out.push({ ...it, _depth: depth });
      if (it.node_type === 'package') walk(it.id, depth + 1);
    });
  };
  walk('root', 0);
  return out;
};

const BOQTree = ({ items, onDelete }) => {
  const rows = flattenBOQ(items);
  return rows.map((item) => {
    const isPkg = item.node_type === 'package';
    return (
      <tr
        key={item.id}
        className={`${isPkg ? 'bg-indigo-50/40 font-semibold' : ''} border-b hover:bg-gray-50`}
        data-testid={`boq-row-${item.id}`}
      >
        <td className="p-2.5">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${item._depth * 16}px` }}>
            {isPkg && <ChevronDown className="w-3.5 h-3.5" />}
            <code className="text-xs">{item.item_code || '—'}</code>
          </div>
        </td>
        <td className="p-2.5">{item.description}</td>
        <td className="p-2.5 text-xs text-gray-500">{item.unit || '—'}</td>
        <td className="p-2.5 text-right">{isPkg ? '' : fmt(item.contract_qty)}</td>
        <td className="p-2.5 text-right">{isPkg ? '' : fmt(item.unit_rate)}</td>
        <td className="p-2.5 text-right font-medium">{isPkg ? '' : fmt(item.total_value)}</td>
        <td className="p-2.5 text-center">
          {!isPkg && (
            <span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_COLORS[item.status] || ''}`}>
              {item.status}
            </span>
          )}
        </td>
        <td className="p-2.5 text-center text-xs text-gray-500">
          {isPkg ? '—' : (item.procurement_strategy || '—')}
        </td>
        <td className="p-2.5 text-right">
          <button data-testid={`boq-delete-${item.id}`} onClick={() => onDelete(item.id)}
                  className="text-red-500 hover:text-red-700 p-1">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>
    );
  });
};

// ─── New BOQ Set Modal ─────────────────────────────────────────────────────
const NewSetModal = ({ projectId, hasMain, onClose, onCreated }) => {
  const [setType, setSetType] = useState(hasMain ? 'variation' : 'main');
  const [code, setCode] = useState(hasMain ? 'VO-001' : 'MAIN');
  const [title, setTitle] = useState('');

  const submit = async () => {
    try {
      await axiosInstance.post(`/pece/projects/${projectId}/boq-sets`, {
        set_type: setType, code, title: title || code,
      });
      toast.success('تم إنشاء BOQ');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل'); }
  };

  return (
    <Modal onClose={onClose} title="BOQ جديد">
      <Field label="نوع">
        <select value={setType} onChange={(e) => setSetType(e.target.value)}
                disabled={!hasMain} className="form-input w-full"
                data-testid="new-set-type">
          {!hasMain && <option value="main">Main</option>}
          <option value="variation">Variation Order</option>
        </select>
      </Field>
      <Field label="الكود (e.g. MAIN, VO-001)">
        <input type="text" value={code} onChange={(e) => setCode(e.target.value)}
               className="form-input w-full" data-testid="new-set-code" />
      </Field>
      <Field label="العنوان">
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
               className="form-input w-full" data-testid="new-set-title" />
      </Field>
      <div className="flex gap-2 mt-4">
        <button onClick={submit} data-testid="new-set-submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded-md flex-1">إنشاء</button>
        <button onClick={onClose} className="border px-4 py-2 rounded-md">إلغاء</button>
      </div>
    </Modal>
  );
};

// ─── New BOQ Item Modal ────────────────────────────────────────────────────
const NewItemModal = ({ projectId, setId, items, onClose, onCreated }) => {
  const packages = items.filter((i) => i.node_type === 'package');
  const [form, setForm] = useState({
    node_type: 'item', parent_id: '', item_code: '', description: '',
    category: '', unit: '', contract_qty: '', unit_rate: '',
    procurement_strategy: '',
  });

  const submit = async () => {
    if (!form.description.trim()) { toast.error('الوصف مطلوب'); return; }
    const payload = {
      node_type: form.node_type,
      parent_id: form.parent_id || null,
      item_code: form.item_code || null,
      description: form.description,
      category: form.category || null,
      unit: form.unit || null,
      contract_qty: Number(form.contract_qty || 0),
      unit_rate: Number(form.unit_rate || 0),
      procurement_strategy: form.procurement_strategy || null,
    };
    try {
      await axiosInstance.post(`/pece/projects/${projectId}/boq-sets/${setId}/items`, payload);
      toast.success('تمت الإضافة');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل'); }
  };

  return (
    <Modal onClose={onClose} title="بند BOQ جديد">
      <div className="grid grid-cols-2 gap-3">
        <Field label="النوع">
          <select value={form.node_type} onChange={(e) => setForm({ ...form, node_type: e.target.value })}
                  className="form-input w-full" data-testid="new-item-type">
            <option value="item">بند</option>
            <option value="package">حزمة (Package)</option>
          </select>
        </Field>
        <Field label="الحزمة الأم">
          <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })}
                  className="form-input w-full" data-testid="new-item-parent">
            <option value="">— لا شيء —</option>
            {packages.map((p) => <option key={p.id} value={p.id}>{p.description}</option>)}
          </select>
        </Field>
        <Field label="الكود">
          <input type="text" value={form.item_code} onChange={(e) => setForm({ ...form, item_code: e.target.value })}
                 className="form-input w-full" data-testid="new-item-code" />
        </Field>
        <Field label="الفئة">
          <input type="text" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                 className="form-input w-full" />
        </Field>
        <div className="col-span-2">
          <Field label="الوصف *">
            <input type="text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                   className="form-input w-full" data-testid="new-item-desc" required />
          </Field>
        </div>
        {form.node_type === 'item' && (
          <>
            <Field label="الوحدة">
              <input type="text" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}
                     className="form-input w-full" placeholder="M, M2, Ton..." />
            </Field>
            <Field label="استراتيجية الشراء">
              <select value={form.procurement_strategy}
                      onChange={(e) => setForm({ ...form, procurement_strategy: e.target.value })}
                      className="form-input w-full" data-testid="new-item-strategy">
                <option value="">—</option>
                {PROC_STRATS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="الكمية التعاقدية">
              <input type="number" value={form.contract_qty} onChange={(e) => setForm({ ...form, contract_qty: e.target.value })}
                     className="form-input w-full" data-testid="new-item-qty" />
            </Field>
            <Field label="سعر الوحدة">
              <input type="number" value={form.unit_rate} onChange={(e) => setForm({ ...form, unit_rate: e.target.value })}
                     className="form-input w-full" data-testid="new-item-rate" />
            </Field>
          </>
        )}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={submit} data-testid="new-item-submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded-md flex-1">إضافة</button>
        <button onClick={onClose} className="border px-4 py-2 rounded-md">إلغاء</button>
      </div>
    </Modal>
  );
};

// ─── Payment Terms Section ─────────────────────────────────────────────────
const PaymentTermsSection = ({ projectId, contractValue, onChange }) => {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const r = await axiosInstance.get(`/pece/projects/${projectId}/payment-terms`);
    setTerms(r.data.terms || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const totalPct = terms.reduce((s, t) => s + Number(t.percentage || 0), 0);
  const isValid = Math.abs(totalPct - 100) < 0.01;

  const updateTerm = (i, patch) => {
    const next = [...terms];
    next[i] = { ...next[i], ...patch };
    // recompute value preview
    next[i].value = (Number(next[i].percentage || 0) * Number(contractValue || 0)) / 100;
    setTerms(next);
  };

  const addTerm = () => setTerms([...terms, { milestone: 'other', percentage: 0, value: 0 }]);
  const removeTerm = (i) => setTerms(terms.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      await axiosInstance.put(`/pece/projects/${projectId}/payment-terms`, {
        terms: terms.map((t, i) => ({
          milestone: t.milestone, label: t.label || null,
          percentage: Number(t.percentage || 0),
          description: t.description || null, sort_order: i,
        })),
      });
      toast.success('تم حفظ شروط الدفع');
      await load(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || 'فشل الحفظ'); }
  };

  if (loading) return <div>...</div>;

  return (
    <div className="bg-white rounded-xl p-6 border space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">شروط الدفع</h3>
        <div className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-2 ${
          isValid ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
        }`} data-testid="payment-terms-total">
          {isValid ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          المجموع: {totalPct.toFixed(2)}%
        </div>
      </div>

      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="text-left p-2">Milestone</th>
            <th className="text-left p-2">Label</th>
            <th className="text-right p-2">%</th>
            <th className="text-right p-2">Value (SAR)</th>
            <th className="text-right p-2"></th>
          </tr>
        </thead>
        <tbody>
          {terms.map((t, i) => (
            <tr key={i} className="border-b" data-testid={`payment-term-${i}`}>
              <td className="p-2">
                <select value={t.milestone} onChange={(e) => updateTerm(i, { milestone: e.target.value })}
                        className="form-input w-full text-sm">
                  {MILESTONES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </td>
              <td className="p-2">
                <input type="text" value={t.label || ''} onChange={(e) => updateTerm(i, { label: e.target.value })}
                       className="form-input w-full text-sm" />
              </td>
              <td className="p-2">
                <input type="number" step="0.01" value={t.percentage}
                       data-testid={`payment-term-pct-${i}`}
                       onChange={(e) => updateTerm(i, { percentage: e.target.value })}
                       className="form-input w-24 text-right text-sm" />
              </td>
              <td className="p-2 text-right text-gray-700">{fmt(t.value)}</td>
              <td className="p-2 text-right">
                <button onClick={() => removeTerm(i)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex gap-2">
        <button onClick={addTerm} data-testid="add-payment-term-btn"
                className="border border-dashed px-4 py-2 rounded-md text-sm hover:bg-gray-50">
          <Plus className="w-4 h-4 inline" /> إضافة بند
        </button>
        <button onClick={save} data-testid="save-payment-terms-btn"
                disabled={!isValid && terms.length > 0}
                className="bg-indigo-600 text-white px-5 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50">
          حفظ
        </button>
      </div>

      {!isValid && terms.length > 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
          ⚠️ لا يمكن حفظ شروط الدفع حتى يصبح المجموع = 100% تماماً.
        </p>
      )}
    </div>
  );
};

// ─── Team Section ──────────────────────────────────────────────────────────
const TeamSection = ({ projectId, onChange }) => {
  const [members, setMembers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ role: 'project_manager', employee_name: '', start_date: '' });

  const load = async () => {
    const r = await axiosInstance.get(`/pece/projects/${projectId}/team`);
    setMembers(r.data);
  };
  useEffect(() => { load(); }, [projectId]);

  const add = async () => {
    if (!form.employee_name.trim()) { toast.error('الاسم مطلوب'); return; }
    try {
      await axiosInstance.post(`/pece/projects/${projectId}/team`, {
        role: form.role, employee_name: form.employee_name,
        start_date: form.start_date || null,
      });
      toast.success('تمت الإضافة');
      setForm({ role: 'project_manager', employee_name: '', start_date: '' });
      setShowAdd(false);
      await load(); onChange();
    } catch (e) { toast.error('فشل'); }
  };

  const remove = async (id) => {
    if (!window.confirm('حذف العضو؟')) return;
    await axiosInstance.delete(`/pece/projects/${projectId}/team/${id}`);
    await load(); onChange();
  };

  return (
    <div className="bg-white rounded-xl p-6 border space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">فريق المشروع</h3>
        <button onClick={() => setShowAdd(true)} data-testid="team-add-btn"
                className="bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1">
          <Plus className="w-4 h-4" /> إضافة عضو
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {members.map((m) => (
          <div key={m.id} className="border rounded-lg p-3 flex items-center justify-between"
               data-testid={`team-member-${m.id}`}>
            <div>
              <div className="text-xs uppercase tracking-wider text-indigo-600 font-semibold">{m.role.replace('_', ' ')}</div>
              <div className="font-semibold">{m.employee_name}</div>
              {m.start_date && <div className="text-xs text-gray-500 mt-0.5">منذ: {m.start_date}</div>}
            </div>
            <button onClick={() => remove(m.id)} className="text-red-500 hover:text-red-700 p-1">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {members.length === 0 && (
          <div className="col-span-full text-center py-8 text-gray-500">لم يُعيّن فريق بعد</div>
        )}
      </div>

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title="إضافة عضو">
          <Field label="الدور">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="form-input w-full" data-testid="team-form-role">
              {TEAM_ROLES.map((r) => <option key={r} value={r}>{r.replace('_', ' ')}</option>)}
            </select>
          </Field>
          <Field label="الاسم">
            <input type="text" value={form.employee_name}
                   onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                   className="form-input w-full" data-testid="team-form-name" />
          </Field>
          <Field label="تاريخ البدء">
            <input type="date" value={form.start_date}
                   onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                   className="form-input w-full" />
          </Field>
          <div className="flex gap-2 mt-4">
            <button onClick={add} data-testid="team-form-submit"
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md flex-1">إضافة</button>
            <button onClick={() => setShowAdd(false)} className="border px-4 py-2 rounded-md">إلغاء</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Modal Helper ──────────────────────────────────────────────────────────
const Modal = ({ onClose, title, children }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto"
         onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"
                data-testid="modal-close">×</button>
      </div>
      {children}
    </div>
  </div>
);

export default PECEWorkspace;
